import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Shake an element to report a rejection.
 *
 * Spread the returned props onto the thing that was rejected — the field, the
 * OTP row, the form — and it shakes whenever `trigger` changes to a new value.
 * Pass no `trigger` and call `shake()` directly instead.
 *
 * Why a trigger *value* rather than a boolean: a rejection has to be repeatable.
 * A flag that is already `true` produces no change on the second wrong password,
 * which is exactly the attempt that most needs the feedback. Redux carries the
 * value as `authRejections`, an ever-increasing count.
 *
 * The class is dropped on `animationend` so the animation can be restarted; a
 * class that stays on cannot replay, and remounting the input to force it would
 * lose the caret and the field's own focus.
 *
 * @returns `{ shakeProps, shake }` — `shakeProps` carries `className` and
 *          `onAnimationEnd`, so merge them if the element already has either.
 */
export const useShake = (trigger) => {
  const [shaking, setShaking] = useState(false);
  // Seeded with the trigger's first value so mounting after a previous failure
  // does not shake on arrival.
  const seen = useRef(trigger);

  const shake = useCallback(() => setShaking(true), []);

  useEffect(() => {
    if (trigger === undefined || trigger === seen.current) return;
    seen.current = trigger;
    // Restart from a clean slate: if a shake is still running, dropping the class
    // for one frame is what lets the next one begin.
    setShaking(false);
    const id = requestAnimationFrame(() => setShaking(true));
    return () => cancelAnimationFrame(id);
  }, [trigger]);

  return {
    shake,
    shakeProps: {
      className: shaking ? 'is-rejected' : undefined,
      onAnimationEnd: shaking ? () => setShaking(false) : undefined,
    },
  };
};

export default useShake;
